/**
 * Axios client + types for the admin portal API.
 *
 * Wire contract: docs/specs/0005-admin-portal-spa-scaffold/spec.md §5.1
 * (mirrors apps/core/senti_core/admin_api/views.py — the server is the
 * authority).
 *
 * Two pieces of behaviour live here:
 *
 *  1. **Cookies are sent on every request.** `withCredentials: true`
 *     ensures the HttpOnly session cookie reaches the API.
 *  2. **CSRF double-submit on every state-changing request.** A request
 *     interceptor reads the CSRF cookie (whichever name is present:
 *     dev uses `senti_admin_api_csrf`, production uses
 *     `__Host-senti_admin_api_csrf`) and echoes it as `X-CSRFToken`.
 *     Server-side `AdminSessionAuthentication._enforce_csrf` rejects
 *     state-changing requests that don't match.
 *
 * 401 handling: a single global response interceptor dispatches an
 * `auth-revoked` CustomEvent. The router's root listener routes the
 * user back to /login, preserving the current location as a `next`
 * query param.
 *
 * **Important carve-out** (issue #143): the auth endpoints overload
 * 401 to mean "wrong code you typed" rather than "your session is
 * gone." Specifically `POST /auth/login`, `POST /auth/totp/confirm`,
 * `POST /auth/totp/verify`, and `POST /auth/recovery/verify` each
 * return ``401 {"error": "invalid_credentials"|"invalid_code"}`` for
 * wrong-input. Treating those as "session revoked" would bounce the
 * operator to /login mid-MFA-flow without ever seeing the inline
 * error. The interceptor discriminates by reading ``body.error`` and
 * only fires ``auth-revoked`` when the 401 is NOT one of the known
 * input-error codes. Session-gone 401s carry ``{"detail": "..."}``
 * (DRF's standard auth-failure shape) and don't match the allowlist
 * so they fire correctly.
 */
import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from "axios";
import { readBrowserCookie } from "./cookies";

const SAFE_METHODS = new Set(["get", "head", "options", "trace"]);

const CSRF_COOKIE_NAMES = [
  "senti_admin_api_csrf",
  "__Host-senti_admin_api_csrf",
] as const;

/** Custom event dispatched when the API replies 401. */
export const AUTH_REVOKED_EVENT = "senti:auth-revoked" as const;

/**
 * Error codes returned in 401 responses that mean "the input you
 * typed was wrong" rather than "your session is gone."
 *
 * Backed by ``apps/core/senti_core/admin_api/views.py``:
 *
 *   - ``invalid_credentials`` — ``/auth/login`` wrong password.
 *   - ``invalid_code``         — ``/auth/totp/confirm``
 *                                ``/auth/totp/verify``
 *                                ``/auth/recovery/verify``
 *
 * These 401s leave the SPA session intact; the user should stay on
 * the current page and see an inline error. Session-gone 401s use
 * DRF's standard ``{"detail": "..."}`` shape and are NOT in this
 * allowlist, so the interceptor correctly routes them to /login.
 */
const INPUT_ERROR_401_CODES = new Set([
  "invalid_credentials",
  "invalid_code",
]);

/**
 * Decide whether a 401 response should trigger the global
 * ``auth-revoked`` event (→ navigate to /login + clear the cached
 * principal). Exported for unit testing; in production the response
 * interceptor in ``buildClient`` is the only caller.
 *
 * Returns ``true`` for every 401 EXCEPT ones whose JSON body carries
 * an ``error`` field in :data:`INPUT_ERROR_401_CODES`. A 401 without
 * a JSON body, or with a body shape we don't recognise, defaults to
 * ``true`` — better to over-route to /login on an unknown error than
 * to silently swallow a session-gone signal.
 */
export function shouldDispatchAuthRevoked(err: AxiosError): boolean {
  if (err.response?.status !== 401) return false;
  const data = err.response.data as unknown;
  if (data && typeof data === "object") {
    const errorCode = (data as { error?: unknown }).error;
    if (typeof errorCode === "string" && INPUT_ERROR_401_CODES.has(errorCode)) {
      return false;
    }
  }
  return true;
}

export function attachCsrfHeader(
  config: InternalAxiosRequestConfig,
  cookieReader: (...names: readonly string[]) => string | null = readBrowserCookie,
): InternalAxiosRequestConfig {
  const method = (config.method ?? "get").toLowerCase();
  if (SAFE_METHODS.has(method)) return config;
  const token = cookieReader(...CSRF_COOKIE_NAMES);
  if (token) {
    config.headers = config.headers ?? {};
    config.headers["X-CSRFToken"] = token;
  }
  return config;
}

export function buildClient(
  baseURL: string = import.meta.env.VITE_ADMIN_API_BASE ?? "/admin-api/v1",
): AxiosInstance {
  const client = axios.create({
    baseURL,
    withCredentials: true,
    // We want to inspect 4xx bodies (e.g. for the 400/min_length on
    // change-password); axios' default of throwing on >=400 is fine
    // because we surface the body via err.response.data in pages.
    timeout: 15_000,
    headers: { Accept: "application/json", "Content-Type": "application/json" },
  });

  client.interceptors.request.use((cfg) => attachCsrfHeader(cfg));

  client.interceptors.response.use(
    (resp) => resp,
    (err: AxiosError) => {
      if (shouldDispatchAuthRevoked(err) && typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent(AUTH_REVOKED_EVENT));
      }
      return Promise.reject(err);
    },
  );

  return client;
}

export const api = buildClient();

// ---------------------------------------------------------------------
// Wire-shape types — mirror admin_api/views.py response bodies.
// ---------------------------------------------------------------------

export type LoginNextStep = "totp_enroll" | "totp_verify" | "change_password";

export interface LoginResponse {
  next_step: LoginNextStep;
}

export interface ApiErrorBody {
  error: string;
  /** Human-readable elaboration. Most spec 0006 §5.1 endpoints set this. */
  detail?: string;
  next_step?: string;
  min_length?: number;
  /** Which request field failed validation (spec 0009 forgot/reset). */
  field?: string;
}

export interface TotpEnrollResponse {
  provisioning_uri: string;
  secret_b32: string;
  qr_png_base64: string;
  recovery_codes: string[];
}

export interface OkResponse {
  ok: true;
}

export type AdminRole = "super" | "tenant";

// All `id` fields below are ULID primary-key strings (e.g.
// "adu_01HK...", "tnt_01HK..."), NOT numbers — see the backend's
// `ULIDField` on AdminUser/Tenant/AdminSession. Typing them as `number`
// previously type-checked but silently broke any downstream code that
// did arithmetic, ordering, or numeric equality on the ID.
export interface MeTenant {
  id: string;
  name: string;
  environment: string;
}

export interface MeSession {
  id: string;
  expires_at: string;
  issued_at: string;
}

export interface MeResponse {
  id: string;
  email: string;
  display_name: string;
  role: AdminRole;
  must_change_password: boolean;
  totp_enrolled: boolean;
  session: MeSession;
  tenants: MeTenant[];
  scoped_to_all_tenants: boolean;
  server_time: string;
}

// ---------------------------------------------------------------------
// Endpoint helpers — thin wrappers so call sites stay readable.
// ---------------------------------------------------------------------

export async function postLogin(
  body: { email: string; password: string },
  cfg?: AxiosRequestConfig,
): Promise<LoginResponse> {
  const r = await api.post<LoginResponse>("/auth/login", body, cfg);
  return r.data;
}

export async function postLogout(): Promise<OkResponse> {
  const r = await api.post<OkResponse>("/auth/logout", {});
  return r.data;
}

export async function postTotpEnroll(): Promise<TotpEnrollResponse> {
  const r = await api.post<TotpEnrollResponse>("/auth/totp/enroll", {});
  return r.data;
}

export async function postTotpConfirm(code: string): Promise<OkResponse> {
  const r = await api.post<OkResponse>("/auth/totp/confirm", { code });
  return r.data;
}

export async function postTotpVerify(code: string): Promise<OkResponse> {
  const r = await api.post<OkResponse>("/auth/totp/verify", { code });
  return r.data;
}

export async function postRecoveryVerify(code: string): Promise<OkResponse> {
  const r = await api.post<OkResponse>("/auth/recovery/verify", { code });
  return r.data;
}

export async function postChangePassword(body: {
  old_password: string;
  new_password: string;
}): Promise<OkResponse> {
  const r = await api.post<OkResponse>("/auth/password/change", body);
  return r.data;
}

/**
 * Self-service reset (spec 0009). Both endpoints are unauthenticated.
 * `forgot` answers 202 for every plausible email, so the response never
 * reveals whether an account exists; 404 means the feature is off.
 */
export async function postForgotPassword(body: {
  email: string;
}): Promise<{ status: "accepted" }> {
  const r = await api.post<{ status: "accepted" }>("/auth/password/forgot", body);
  return r.data;
}

export async function postResetPassword(body: {
  token: string;
  new_password: string;
}): Promise<{ next_step: "login" }> {
  const r = await api.post<{ next_step: "login" }>("/auth/password/reset", body);
  return r.data;
}

export async function getMe(): Promise<MeResponse> {
  const r = await api.get<MeResponse>("/me");
  return r.data;
}

/** Narrow helper: pull the structured error body out of an axios error. */
export function asApiError(err: unknown): ApiErrorBody | null {
  if (axios.isAxiosError(err) && err.response?.data) {
    const data = err.response.data as Partial<ApiErrorBody>;
    if (typeof data.error === "string") {
      return data as ApiErrorBody;
    }
  }
  return null;
}

// ---------------------------------------------------------------------
// Tenants (spec 0006 §5.1)
// ---------------------------------------------------------------------

export type TenantEnvironment = "test" | "live";
export type TenantStatus = "active" | "suspended";

export interface TenantCounts {
  api_keys: number;
  provider_accounts: number;
  operators: number;
}

/**
 * Per-tenant feature-flag map. Keys are flag names; values are bool.
 * The server's serializer always populates every KNOWN flag (defaulting
 * to ``false``) so consumers can render checkboxes without
 * client-side knowledge of the registry.
 */
export type FeatureFlags = Record<string, boolean>;

export interface TenantRow {
  id: string;
  name: string;
  environment: TenantEnvironment;
  status: TenantStatus;
  created_at: string;
  deleted_at: string | null;
  counts?: TenantCounts;
  // Spec 0007 follow-up — every known flag name with a current bool.
  // Optional on the type for backward-compat with older endpoint
  // shapes / tests; absent → no editor renders.
  feature_flags?: FeatureFlags;
  // Surfaced separately so the UI can render a "legacy keys" warning
  // (typos / pre-whitelist artefacts) without polluting the canonical
  // editor. Values are intentionally `unknown` — operators eyeball
  // and clean up via a migration.
  feature_flags_unknown?: Record<string, unknown>;
}

export interface PaginatedResponse<T> {
  results: T[];
  page: number;
  page_size: number;
  total: number;
  has_next: boolean;
}

export interface TenantListFilters {
  page?: number;
  page_size?: number;
  environment?: TenantEnvironment;
  status?: TenantStatus;
  include_deleted?: boolean;
}

export async function getTenants(
  filters: TenantListFilters = {},
): Promise<PaginatedResponse<TenantRow>> {
  const params: Record<string, string | number> = {};
  if (filters.page !== undefined) params.page = filters.page;
  if (filters.page_size !== undefined) params.page_size = filters.page_size;
  if (filters.environment) params.environment = filters.environment;
  if (filters.status) params.status = filters.status;
  if (filters.include_deleted) params.include_deleted = "true";
  const r = await api.get<PaginatedResponse<TenantRow>>("/tenants/", { params });
  return r.data;
}

export async function getTenant(id: string): Promise<TenantRow> {
  const r = await api.get<TenantRow>(`/tenants/${id}/`);
  return r.data;
}

export async function postTenant(body: {
  name: string;
  environment: TenantEnvironment;
}): Promise<TenantRow> {
  const r = await api.post<TenantRow>("/tenants/", body);
  return r.data;
}

export async function patchTenant(
  id: string,
  body: {
    name?: string;
    status?: TenantStatus;
    // Partial-merge: only flags present in this object change.
    // Server rejects unknown flag names (typo defence) and non-bool
    // values with 400.
    feature_flags?: FeatureFlags;
  },
): Promise<TenantRow> {
  const r = await api.patch<TenantRow>(`/tenants/${id}/`, body);
  return r.data;
}

export async function deleteTenant(id: string): Promise<void> {
  await api.delete(`/tenants/${id}/`);
}

export async function restoreTenant(id: string): Promise<TenantRow> {
  const r = await api.post<TenantRow>(`/tenants/${id}/restore/`, {});
  return r.data;
}

// ---------------------------------------------------------------------
// Payments observability (spec 0006 PR-5)
// ---------------------------------------------------------------------

export type IntentDirection = "collect" | "payout" | "refund";

export type IntentState =
  | "requires_action"
  | "processing"
  | "succeeded"
  | "failed"
  | "cancelled"
  | "refunding"
  | "refunded";

// ``unknown``: the dispatch call ended without proof either way (spec
// 0010 §5.2.1); the money may have moved. Settled by callback or operator.
export type ProviderTxStatus = "pending" | "succeeded" | "failed" | "unknown";

export interface PaymentIntentRow {
  id: string;
  tenant_id: string;
  tenant_name: string;
  tenant_environment: TenantEnvironment;
  direction: IntentDirection;
  method: string;
  amount: string; // minor units, string for big-int precision
  currency: string;
  state: IntentState;
  provider_status: ProviderTxStatus | null;
  created_at: string;
  updated_at: string;
}

export interface ProviderTransactionRow {
  id: string;
  plugin: string;
  provider_account_id: string;
  provider_ref: string;
  status: ProviderTxStatus;
  error_code: string | null;
  provider_error_code: string | null;
  reconciled_at: string | null;
  reconciliation_source: string | null;
  reconciled_amount: string | null;
  request_payload: Record<string, unknown>;
  response_payload: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

export interface PaymentIntentEvent {
  sequence: number;
  event_type: string;
  payload: Record<string, unknown>;
  created_at: string;
}

export interface PaymentIntentDetail extends PaymentIntentRow {
  idempotency_key: string;
  counterparty: Record<string, unknown>;
  metadata: Record<string, unknown>;
  refund_of_id: string | null;
  request_snapshot: Record<string, unknown>;
  response_snapshot: Record<string, unknown> | null;
  provider_transaction: ProviderTransactionRow | null;
  events: PaymentIntentEvent[];
  events_truncated: boolean;
}

export interface PaymentIntentListFilters {
  page?: number;
  page_size?: number;
  tenant_id?: string;
  environment?: TenantEnvironment;
  status?: IntentState;
  direction?: IntentDirection;
  method?: string;
  date_from?: string; // ISO-8601
  date_to?: string;
  q?: string;
}

function toParams(filters: Record<string, unknown>): Record<string, string | number> {
  const params: Record<string, string | number> = {};
  for (const [k, v] of Object.entries(filters)) {
    if (v === undefined || v === null || v === "") continue;
    if (typeof v === "number" || typeof v === "string") params[k] = v;
  }
  return params;
}

export async function getPaymentIntents(
  filters: PaymentIntentListFilters = {},
): Promise<PaginatedResponse<PaymentIntentRow>> {
  const r = await api.get<PaginatedResponse<PaymentIntentRow>>("/payment-intents/", {
    params: toParams(filters as Record<string, unknown>),
  });
  return r.data;
}

export async function getPaymentIntent(id: string): Promise<PaymentIntentDetail> {
  const r = await api.get<PaymentIntentDetail>(`/payment-intents/${id}/`);
  return r.data;
}

// --- payment intent: sync-status (PR-7b admin mirror of spec 0004) ---

/** Discriminator on the wire — exactly the documented
 * :class:`StatusQueryOutcome` values from spec 0004 §5.1.1. */
export type SyncStatusOutcome =
  | "sync_settled"
  | "noop_terminal"
  | "async_dispatched"
  | "recorded_pending"
  | "dispatch_in_flight"
  | "missing_provider_tx"
  | "incompatible_state"
  | "unsupported"
  | "routing_failed"
  | "plugin_unavailable"
  | "upstream_failed";

export interface SyncPaymentIntentStatusResponse {
  intent_id: string;
  outcome: SyncStatusOutcome;
  query_id?: string;
  /** Intent state AFTER the operation (when the outcome touched the intent). */
  state?: IntentState;
  result_code?: string;
  result_desc?: string;
  error?: string;
}

export async function syncPaymentIntentStatus(
  id: string,
  body: { reason: string },
): Promise<SyncPaymentIntentStatusResponse> {
  const r = await api.post<SyncPaymentIntentStatusResponse>(
    `/payment-intents/${id}/sync-status/`,
    body,
  );
  return r.data;
}

// ---- InboundPayment ------------------------------------------------

export type InboundPaymentState = "received" | "reversed";

export interface InboundPaymentRow {
  id: string;
  tenant_id: string;
  tenant_name: string;
  tenant_environment: TenantEnvironment;
  plugin: string;
  provider_account_id: string;
  provider_ref: string;
  amount: string;
  currency: string;
  account_ref: string;
  state: InboundPaymentState;
  received_at: string;
  payer_msisdn_masked: string;
  created_at: string;
}

export interface InboundPaymentDetail {
  id: string;
  tenant_id: string;
  tenant_name: string;
  tenant_environment: TenantEnvironment;
  plugin: string;
  provider_account_id: string;
  provider_ref: string;
  amount: string;
  currency: string;
  account_ref: string;
  state: InboundPaymentState;
  received_at: string;
  created_at: string;
  updated_at: string;
  reverses_id: string | null;
  inbox_message_id: string;
  payer: Record<string, unknown>;
}

export interface InboundPaymentListFilters {
  page?: number;
  page_size?: number;
  tenant_id?: string;
  environment?: TenantEnvironment;
  state?: InboundPaymentState;
  plugin?: string;
  account_ref?: string;
  date_from?: string;
  date_to?: string;
  q?: string;
}

export async function getInboundPayments(
  filters: InboundPaymentListFilters = {},
): Promise<PaginatedResponse<InboundPaymentRow>> {
  const r = await api.get<PaginatedResponse<InboundPaymentRow>>("/inbound-payments/", {
    params: toParams(filters as Record<string, unknown>),
  });
  return r.data;
}

export async function getInboundPayment(id: string): Promise<InboundPaymentDetail> {
  const r = await api.get<InboundPaymentDetail>(`/inbound-payments/${id}/`);
  return r.data;
}

// --- inbound payment: reveal-payer with step-up TOTP -----------------

export interface InboundPaymentRevealPayerResponse {
  ok: true;
  /** Unmasked payer dict — MSISDN + name + any other fields the
   * plugin parser captured. Surface with care; an audit row has
   * already been written. */
  payer: Record<string, unknown>;
}

export async function revealInboundPaymentPayer(
  id: string,
  body: { totp_code: string; reason: string },
): Promise<InboundPaymentRevealPayerResponse> {
  const r = await api.post<InboundPaymentRevealPayerResponse>(
    `/inbound-payments/${id}/reveal-payer/`,
    body,
  );
  return r.data;
}

// ---------------------------------------------------------------------
// Operators / admin-users (spec 0006 §5.1 — PR-4)
// ---------------------------------------------------------------------

export interface OperatorTenant {
  id: string;
  name: string;
  environment: string;
}

export interface OperatorRow {
  id: string;
  email: string;
  display_name: string;
  role: AdminRole;
  is_active: boolean;
  totp_enrolled: boolean;
  must_change_password: boolean;
  last_login_at: string | null;
  created_at: string;
  tenants: OperatorTenant[];
}

export interface InviteResponse {
  operator: OperatorRow;
  /** Shown ONCE in the temp-password modal — never persisted. */
  temp_password: string;
  email_dispatched: boolean;
}

export interface RotatePasswordResponse {
  temp_password: string;
  email_dispatched: boolean;
}

export interface OperatorListFilters {
  page?: number;
  page_size?: number;
  role?: AdminRole;
  is_active?: boolean;
  tenant_id?: string;
}

export async function getOperators(
  filters: OperatorListFilters = {},
): Promise<PaginatedResponse<OperatorRow>> {
  const params: Record<string, string | number> = {};
  if (filters.page !== undefined) params.page = filters.page;
  if (filters.page_size !== undefined) params.page_size = filters.page_size;
  if (filters.role) params.role = filters.role;
  if (filters.is_active !== undefined) params.is_active = String(filters.is_active);
  if (filters.tenant_id) params.tenant_id = filters.tenant_id;
  const r = await api.get<PaginatedResponse<OperatorRow>>("/admin-users/", { params });
  return r.data;
}

export async function getOperator(id: string): Promise<OperatorRow> {
  const r = await api.get<OperatorRow>(`/admin-users/${id}/`);
  return r.data;
}

export async function postOperator(body: {
  email: string;
  display_name?: string;
  role: AdminRole;
  /** Required when role=tenant; rejected when role=super. */
  tenant_ids?: string[];
  /** Defaults to true server-side. Pass false to skip email dispatch. */
  send_email?: boolean;
}): Promise<InviteResponse> {
  const r = await api.post<InviteResponse>("/admin-users/", body);
  return r.data;
}

export async function patchOperator(
  id: string,
  body: {
    display_name?: string;
    role?: AdminRole;
    is_active?: boolean;
  },
): Promise<OperatorRow> {
  const r = await api.patch<OperatorRow>(`/admin-users/${id}/`, body);
  return r.data;
}

export async function rotateOperatorPassword(
  id: string,
  body: { send_email?: boolean } = {},
): Promise<RotatePasswordResponse> {
  const r = await api.post<RotatePasswordResponse>(
    `/admin-users/${id}/rotate-password/`,
    body,
  );
  return r.data;
}

export async function revokeOperatorTotp(id: string): Promise<OkResponse> {
  const r = await api.post<OkResponse>(`/admin-users/${id}/revoke-totp/`, {});
  return r.data;
}

export interface GrantResponse {
  id: string;
  tenant: OperatorTenant;
}

export async function grantOperatorTenant(
  operatorId: string,
  tenantId: string,
): Promise<GrantResponse> {
  const r = await api.post<GrantResponse>(`/admin-users/${operatorId}/tenants/`, {
    tenant_id: tenantId,
  });
  return r.data;
}

export async function revokeOperatorTenant(
  operatorId: string,
  tenantId: string,
): Promise<void> {
  await api.delete(`/admin-users/${operatorId}/tenants/${tenantId}/`);
}

// ---------------------------------------------------------------------
// API keys (PR-4b)
// ---------------------------------------------------------------------

export type ApiKeyKind = "pk" | "sk" | "ak";

export interface ApiKeyRow {
  id: string;
  prefix: string;
  kind: ApiKeyKind;
  scopes: string[];
  environment: TenantEnvironment;
  last_used_at: string | null;
  revoked_at: string | null;
  created_at: string;
  tenant: { id: string; name: string; environment: string };
}

export interface ApiKeyMintResponse {
  api_key: ApiKeyRow;
  /** Shown ONCE in the reveal modal — never persisted. */
  plaintext: string;
}

export async function getApiKeys(
  filters: { tenant_id?: string; kind?: ApiKeyKind; is_active?: boolean } = {},
): Promise<PaginatedResponse<ApiKeyRow>> {
  const params: Record<string, string | number> = { page_size: 100 };
  if (filters.tenant_id) params.tenant_id = filters.tenant_id;
  if (filters.kind) params.kind = filters.kind;
  if (filters.is_active !== undefined) {
    params.is_active = String(filters.is_active);
  }
  const r = await api.get<PaginatedResponse<ApiKeyRow>>("/api-keys/", { params });
  return r.data;
}

export async function mintApiKey(body: {
  tenant_id: string;
  kind: ApiKeyKind;
}): Promise<ApiKeyMintResponse> {
  const r = await api.post<ApiKeyMintResponse>("/api-keys/", body);
  return r.data;
}

export async function revokeApiKey(id: string): Promise<ApiKeyRow> {
  const r = await api.post<ApiKeyRow>(`/api-keys/${id}/revoke/`, {});
  return r.data;
}

// ---------------------------------------------------------------------
// Provider accounts (PR-4b)
// ---------------------------------------------------------------------

export type ProviderAccountStatus = "active" | "disabled";

export interface ProviderAccountRow {
  id: string;
  plugin: string;
  name: string;
  status: ProviderAccountStatus;
  config: Record<string, unknown>;
  callback_url: string | null;
  created_at: string;
  updated_at: string | null;
  tenant: { id: string; name: string; environment: string };
}

export async function getProviderAccounts(
  filters: { tenant_id?: string; plugin?: string; status?: ProviderAccountStatus } = {},
): Promise<PaginatedResponse<ProviderAccountRow>> {
  const params: Record<string, string | number> = { page_size: 100 };
  if (filters.tenant_id) params.tenant_id = filters.tenant_id;
  if (filters.plugin) params.plugin = filters.plugin;
  if (filters.status) params.status = filters.status;
  const r = await api.get<PaginatedResponse<ProviderAccountRow>>(
    "/provider-accounts/",
    { params },
  );
  return r.data;
}

export async function createProviderAccount(body: {
  tenant_id: string;
  plugin: string;
  name: string;
  credentials: Record<string, unknown>;
}): Promise<ProviderAccountRow> {
  const r = await api.post<ProviderAccountRow>("/provider-accounts/", body);
  return r.data;
}

export async function patchProviderAccount(
  id: string,
  body: { name?: string; status?: ProviderAccountStatus },
): Promise<ProviderAccountRow> {
  const r = await api.patch<ProviderAccountRow>(`/provider-accounts/${id}/`, body);
  return r.data;
}

/** An account that shares this account's initiator (spec 0010 T9). */
export interface InitiatorSibling {
  id: string;
  name: string;
  status: ProviderAccountStatus;
}

/** Detail / create / rotate shape: the row plus the credential-guard
 * block and the accounts sharing the initiator. */
export interface ProviderAccountDetail extends ProviderAccountRow {
  credential?: { state: string; variant: string } | null;
  initiator_siblings?: InitiatorSibling[];
}

export async function getProviderAccount(id: string): Promise<ProviderAccountDetail> {
  const r = await api.get<ProviderAccountDetail>(`/provider-accounts/${id}/`);
  return r.data;
}

export interface RotateProviderSecretsOptions {
  /** Write the same credential to every account sharing the initiator. */
  apply_to_siblings?: boolean;
  /** TOTP step-up: required when Senti encrypts the initiator password
   * or the rotation applies to siblings (spec 0010 §5.6.1). */
  totp_code?: string;
}

export async function rotateProviderAccountSecrets(
  id: string,
  credentials: Record<string, unknown>,
  options: RotateProviderSecretsOptions = {},
): Promise<ProviderAccountDetail & { updated_account_ids?: string[] }> {
  const body: Record<string, unknown> = { credentials };
  if (options.apply_to_siblings) body.apply_to_siblings = true;
  if (options.totp_code) body.totp_code = options.totp_code;
  const r = await api.post<ProviderAccountDetail & { updated_account_ids?: string[] }>(
    `/provider-accounts/${id}/rotate-secrets/`,
    body,
  );
  return r.data;
}

// ---------------------------------------------------------------------
// Operational observability (PR-6a)
// ---------------------------------------------------------------------

// --- callback inbox -------------------------------------------------

export type CallbackInboxState = "pending" | "processed" | "dead_lettered";

export interface CallbackInboxProviderAccount {
  id: string;
  plugin: string;
  name: string;
  tenant: { id: string; name: string; environment: string };
}

export interface CallbackInboxRow {
  id: string;
  plugin: string;
  dedup_key: string;
  state: CallbackInboxState;
  received_at: string;
  processed_at: string | null;
  processing_error: string | null;
  attempts: number;
  raw_headers: Record<string, unknown>;
  provider_account: CallbackInboxProviderAccount | null;
}

export interface CallbackInboxRequeueAudit {
  id: string;
  requeued_at: string;
  actor: string;
  reason: string;
  prior_attempts: number;
  prior_processing_error: string;
}

export interface CallbackInboxDetail extends CallbackInboxRow {
  requeue_audits: CallbackInboxRequeueAudit[];
}

export interface CallbackInboxListFilters {
  page?: number;
  page_size?: number;
  state?: CallbackInboxState;
  plugin?: string;
  tenant_id?: string;
  date_from?: string;
  date_to?: string;
  q?: string;
}

export async function getCallbackInbox(
  filters: CallbackInboxListFilters = {},
): Promise<PaginatedResponse<CallbackInboxRow>> {
  const r = await api.get<PaginatedResponse<CallbackInboxRow>>("/callback-inbox/", {
    params: toParams(filters as Record<string, unknown>),
  });
  return r.data;
}

export async function getCallbackInboxRow(id: string): Promise<CallbackInboxDetail> {
  const r = await api.get<CallbackInboxDetail>(`/callback-inbox/${id}/`);
  return r.data;
}

// --- callback inbox: re-queue (PR-6b) --------------------------------

export interface CallbackInboxRequeueResponse {
  ok: true;
  audit_id: string;
  prior_attempts: number;
  prior_processing_error: string;
}

export async function requeueCallbackInbox(
  id: string,
  body: { reason: string },
): Promise<CallbackInboxRequeueResponse> {
  const r = await api.post<CallbackInboxRequeueResponse>(
    `/callback-inbox/${id}/requeue/`,
    body,
  );
  return r.data;
}

// --- callback inbox: raw-body reveal with step-up TOTP (PR-6b) ------

export interface CallbackInboxRevealBodyResponse {
  ok: true;
  byte_length: number;
  /** Decoded UTF-8 string; null when the bytes weren't valid UTF-8. */
  body_text: string | null;
  /** Base64 of the raw bytes; non-null only when body_text is null. */
  body_base64: string | null;
}

export async function revealCallbackInboxBody(
  id: string,
  body: { totp_code: string; reason: string },
): Promise<CallbackInboxRevealBodyResponse> {
  const r = await api.post<CallbackInboxRevealBodyResponse>(
    `/callback-inbox/${id}/reveal-body/`,
    body,
  );
  return r.data;
}

// --- webhook outbox -------------------------------------------------

export type WebhookOutboxState =
  | "pending"
  | "in_flight"
  | "delivered"
  | "dead_lettered";

export interface WebhookOutboxRow {
  id: string;
  event_type: string;
  dedup_key: string;
  state: WebhookOutboxState;
  attempts: number;
  available_at: string;
  delivered_at: string | null;
  dead_lettered_at: string | null;
  created_at: string;
  tenant: { id: string; name: string; environment: string };
  endpoint: { id: string; url: string; status: string };
  payload: Record<string, unknown>;
}

export interface WebhookDeliveryAttempt {
  id: string;
  attempted_at: string;
  response_status: number | null;
  response_body_truncated: string | null;
  error: string | null;
  duration_ms: number | null;
  /** Exact `X-Senti-Signature` header value sent on this attempt
   * (`t=<unix>,v1=<hex>`). Useful when correlating against a
   * consumer's logs for 4xx triage. Null on historical rows. */
  request_signature: string | null;
}

export interface WebhookOutboxListFilters {
  page?: number;
  page_size?: number;
  state?: WebhookOutboxState;
  tenant_id?: string;
  endpoint_id?: string;
  event_type?: string;
  date_from?: string;
  date_to?: string;
  q?: string;
}

export async function getWebhookOutbox(
  filters: WebhookOutboxListFilters = {},
): Promise<PaginatedResponse<WebhookOutboxRow>> {
  const r = await api.get<PaginatedResponse<WebhookOutboxRow>>("/webhook-outbox/", {
    params: toParams(filters as Record<string, unknown>),
  });
  return r.data;
}

// Detail response is the same shape as the list row plus the
// prefetched per-attempt rows under ``delivery_attempts``.
// ``attempts`` stays an int (counter); the list of per-HTTP-attempt
// rows lives under its own key so the wire shape is consistent
// across list and detail responses.
export interface WebhookOutboxDetailResponse extends WebhookOutboxRow {
  delivery_attempts: WebhookDeliveryAttempt[];
}

export async function getWebhookOutboxRow(
  id: string,
): Promise<WebhookOutboxDetailResponse> {
  const r = await api.get<WebhookOutboxDetailResponse>(`/webhook-outbox/${id}/`);
  return r.data;
}

// --- webhook outbox: retry (PR-6b) -----------------------------------

export interface WebhookOutboxRetryResponse {
  ok: true;
  prior_attempts: number;
  prior_dead_lettered_at: string;
}

export async function retryWebhookOutbox(
  id: string,
  body: { reason: string },
): Promise<WebhookOutboxRetryResponse> {
  const r = await api.post<WebhookOutboxRetryResponse>(
    `/webhook-outbox/${id}/retry/`,
    body,
  );
  return r.data;
}

// --- audit log ------------------------------------------------------

export interface AuditLogActor {
  id: string;
  email: string;
  display_name: string;
  role: AdminRole;
}

export interface AuditLogRow {
  id: string;
  action: string;
  actor: AuditLogActor | null;
  target_type: string;
  target_id: string;
  metadata: Record<string, unknown>;
  request_ip: string | null;
  request_id: string;
  created_at: string;
}

export interface AuditLogListFilters {
  page?: number;
  page_size?: number;
  actor_id?: string;
  action?: string;
  target_type?: string;
  target_id?: string;
  date_from?: string;
  date_to?: string;
  q?: string;
}

export async function getAuditLog(
  filters: AuditLogListFilters = {},
): Promise<PaginatedResponse<AuditLogRow>> {
  const r = await api.get<PaginatedResponse<AuditLogRow>>("/audit-log/", {
    params: toParams(filters as Record<string, unknown>),
  });
  return r.data;
}

// --- balance queries -----------------------------------------------

export type BalanceQueryState = "dispatched" | "settled" | "failed" | "stale";

export interface BalanceQueryAccount {
  label: string;
  amount: number;
  currency: string;
}

export interface BalanceQueryRow {
  id: string;
  state: BalanceQueryState;
  originator_conversation_id: string;
  conversation_id: string | null;
  created_at: string;
  result_received_at: string | null;
  result_code: string | null;
  result_desc: string | null;
  dropped_chunks: number;
  accounts: BalanceQueryAccount[] | null;
  tenant: { id: string; name: string; environment: string };
  provider_account: { id: string; plugin: string; name: string };
  inbox_row_id: string | null;
}

export interface BalanceQueryListFilters {
  page?: number;
  page_size?: number;
  state?: BalanceQueryState;
  tenant_id?: string;
  provider_account_id?: string;
  date_from?: string;
  date_to?: string;
}

export async function getBalanceQueries(
  filters: BalanceQueryListFilters = {},
): Promise<PaginatedResponse<BalanceQueryRow>> {
  const r = await api.get<PaginatedResponse<BalanceQueryRow>>("/balance-queries/", {
    params: toParams(filters as Record<string, unknown>),
  });
  return r.data;
}

export async function getBalanceQuery(id: string): Promise<BalanceQueryRow> {
  const r = await api.get<BalanceQueryRow>(`/balance-queries/${id}/`);
  return r.data;
}

// --- webhook endpoints (spec 0008 §5.6) ----------------------------

export type WebhookEndpointStatus = "active" | "disabled";

/** The event types a subscriber can opt into via the UI. Subset of the
 * backend allowlist in ``views_webhook_endpoints._KNOWN_EVENT_TYPES``.
 * ``inbound.reversed`` is intentionally omitted from the picker until
 * consumers handle reversals (a reversal subscribed today would be
 * mis-applied as a fresh inbound payment). */
export const WEBHOOK_EVENT_TYPES = [
  "intent.created",
  "intent.processing",
  "intent.succeeded",
  "intent.failed",
  "intent.cancelled",
  "intent.refunding",
  "intent.refunded",
  "inbound.received",
] as const;

export interface WebhookEndpointRow {
  id: string;
  url: string;
  enabled_events: string[];
  status: WebhookEndpointStatus;
  /** Whether a signing secret has been provisioned. The secret itself
   * is never returned on a read — only once on create / rotate. */
  has_secret: boolean;
  created_at: string;
  updated_at: string | null;
  tenant: { id: string; name: string; environment: string };
}

/** Create / rotate response — carries the plaintext signing ``secret``
 * exactly once. */
export interface WebhookEndpointSecretResponse {
  webhook_endpoint: WebhookEndpointRow;
  secret: string;
}

export async function getWebhookEndpoints(
  filters: { tenant_id?: string; status?: WebhookEndpointStatus } = {},
): Promise<PaginatedResponse<WebhookEndpointRow>> {
  const params: Record<string, string | number> = { page_size: 100 };
  if (filters.tenant_id) params.tenant_id = filters.tenant_id;
  if (filters.status) params.status = filters.status;
  const r = await api.get<PaginatedResponse<WebhookEndpointRow>>(
    "/webhook-endpoints/",
    { params },
  );
  return r.data;
}

export async function createWebhookEndpoint(body: {
  tenant_id: string;
  url: string;
  enabled_events: string[];
}): Promise<WebhookEndpointSecretResponse> {
  const r = await api.post<WebhookEndpointSecretResponse>(
    "/webhook-endpoints/",
    body,
  );
  return r.data;
}

export async function patchWebhookEndpoint(
  id: string,
  body: {
    url?: string;
    enabled_events?: string[];
    status?: WebhookEndpointStatus;
  },
): Promise<WebhookEndpointRow> {
  const r = await api.patch<WebhookEndpointRow>(
    `/webhook-endpoints/${id}/`,
    body,
  );
  return r.data;
}

export async function rotateWebhookEndpointSecret(
  id: string,
): Promise<WebhookEndpointSecretResponse> {
  const r = await api.post<WebhookEndpointSecretResponse>(
    `/webhook-endpoints/${id}/rotate-secret/`,
    {},
  );
  return r.data;
}

// ---------------------------------------------------------------------
// Payout health (spec 0010 §7, item 22)
// ---------------------------------------------------------------------

export interface PayoutAlertSummary {
  id: string;
  kind: string;
  severity: "critical" | "error";
  tenant_id: string;
  intent_id: string | null;
  count: number;
  last_seen: string;
}

export interface PayoutHealth {
  unresolved_count: number;
  oldest_unresolved_age_s: number | null;
  parked_count: number;
  oldest_parked_age_s: number | null;
  awaiting_approval_count: number;
  open_alerts: number;
  needs_resolution_dead_letters: number;
  banner: "page" | "warn" | null;
  latest_alerts: PayoutAlertSummary[];
}

export async function getPayoutHealth(): Promise<PayoutHealth> {
  const r = await api.get<PayoutHealth>("/payout-health/");
  return r.data;
}
